import type {
  CustomerReservationCalendarDay,
  CustomerReservationMutationResult,
  CustomerReservationReadModel,
  CustomerReservationTransitionAction,
  CustomerReservationWorkspace,
  CustomerReservationWorkspaceItem,
  TableSnapshot,
} from "@billiard/contracts";
import {
  CalendarClock,
  Check,
  Clock3,
  List as ListIcon,
  Phone,
  RefreshCw,
  UsersRound,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type CustomerReservationHistoryPageBoundary,
  type CustomerReservationMutationCommand,
  createCustomerReservation,
  customerReservationOutcomeIsUnknown,
  getCustomerReservationByRequest,
  getCustomerReservationCalendarDay,
  listCustomerReservationHistory,
  listCustomerReservations,
  seatCustomerReservation,
  transitionCustomerReservation,
} from "./api";
import { businessErrorMessage } from "./businessErrorMessage";
import {
  beginCustomerReservationDispatch,
  type CustomerReservationRecovery,
  type CustomerReservationRecoveryView,
  claimCustomerReservationRecovery,
  clearCustomerReservationRecovery,
  customerReservationRecoveryContext,
  finishCustomerReservationDispatch,
  markCustomerReservationOutcomeUnknown,
  readCustomerReservationRecovery,
  reconcileCustomerReservationAuthority,
} from "./customerReservationRecovery";
import { getCustomerSelfServiceBootstrap } from "./customerSelfServiceApi";

type OperatorRole = "OWNER" | "MANAGER" | "CASHIER" | "WAITER" | "COACH";

type CustomerReservationCalendarState = {
  requestKey: string;
  refreshRevision: number;
  status: "LOADING" | "REFRESHING" | "READY" | "STALE" | "ERROR";
  value: CustomerReservationCalendarDay | null;
  error: string | null;
};

const stateLabels: Record<CustomerReservationReadModel["state"], string> = {
  PENDING_CONFIRMATION: "待确认",
  CONFIRMED: "已确认",
  WAITING: "已到店排队",
  CALLED: "已叫号",
  SEATED: "已入座开台",
  CANCELLED: "已取消",
  NO_SHOW: "未到店",
  EXPIRED: "已过期",
};

const actionLabels: Record<CustomerReservationTransitionAction, string> = {
  CONFIRM: "确认预约",
  ARRIVE: "登记到店并排队",
  CALL: "叫号",
  SKIP: "暂时跳过",
  RETRACT_CALL: "撤回误叫号",
  CANCEL: "取消预约",
  NO_SHOW: "登记爽约",
  EXPIRE: "登记过期",
};

const actionConsequences: Record<CustomerReservationTransitionAction, string> = {
  CONFIRM: "确认后表示门店已接受这次预约，顾客到店后才能加入排队。",
  ARRIVE: "登记后顾客进入现场排队，页面会显示队列顺序；本轮不自动分配球台。",
  CALL: "叫号后表示现在轮到这位顾客，请工作人员现场通知并准备选台。",
  SKIP: "顾客暂时无法入座时使用，会移到等待队尾；必须填写原因，避免其他员工误解。",
  RETRACT_CALL: "仅用于工作人员误叫号；撤回后恢复等待，并保留叫号前的队列顺序。",
  CANCEL: "取消后这条预约不能继续排队或入座；必须填写顾客或门店取消原因。",
  NO_SHOW:
    "仅在已确认但顾客没有到店时使用；必须填写核对经过。操作后预约结束，不能再到店排队或入座；本功能当前不扣款、没有订金。",
  EXPIRE: "预约时间已过且不再保留时使用；必须填写处理原因。",
};

const reasonActions = new Set<CustomerReservationTransitionAction>([
  "SKIP",
  "RETRACT_CALL",
  "CANCEL",
  "NO_SHOW",
  "EXPIRE",
]);

function nextIdempotencyKey(): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `customer-reservation:${random}`;
}

const STORE_LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u;

function storeDateTimeParts(value: string | Date, timeZone: string): Record<string, string> {
  return Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(typeof value === "string" ? new Date(value) : value)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
}

function storeLocalDateTimeValue(value: string | Date, timeZone: string): string {
  const parts = storeDateTimeParts(value, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function defaultStartValue(referenceAt: string, timeZone: string): string {
  const oneHourLater = new Date(Date.parse(referenceAt) + 60 * 60_000);
  const local = storeLocalDateTimeValue(oneHourLater, timeZone);
  const match = STORE_LOCAL_DATE_TIME_PATTERN.exec(local);
  if (!match) return "";
  const wallEpoch = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );
  return new Date(Math.ceil(wallEpoch / (30 * 60_000)) * 30 * 60_000).toISOString().slice(0, 16);
}

function storeLocalDateTimeToIso(
  value: string,
  timeZone: string,
): { status: "VALID"; value: string } | { status: "INVALID" | "AMBIGUOUS" } {
  const match = STORE_LOCAL_DATE_TIME_PATTERN.exec(value);
  if (!match) return { status: "INVALID" };
  const wallEpoch = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );
  if (new Date(wallEpoch).toISOString().slice(0, 16) !== value) return { status: "INVALID" };
  const matches: string[] = [];
  for (let offsetMinutes = -14 * 60; offsetMinutes <= 14 * 60; offsetMinutes += 15) {
    const candidate = new Date(wallEpoch - offsetMinutes * 60_000);
    if (storeLocalDateTimeValue(candidate, timeZone) === value)
      matches.push(candidate.toISOString());
  }
  if (matches.length === 0) return { status: "INVALID" };
  if (matches.length > 1) return { status: "AMBIGUOUS" };
  return { status: "VALID", value: matches[0] as string };
}

function displayTime(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function displayClock(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function reservationWaitEstimateText(item: CustomerReservationWorkspaceItem): string | null {
  switch (item.waitEstimateKind) {
    case "READY_NOW":
      return "现在可安排";
    case "CURRENT_IDLE_TABLES":
      return `约 ${item.estimatedWaitMinutes} 分钟；只按当前空闲球台和预约计划时长估算，不保证实际释放时间。`;
    case "UNAVAILABLE_NO_IDLE_TABLE":
      return "在用台结束时间未知，暂无法估算";
    case "NOT_IN_QUEUE":
      return null;
  }
}

function commandLabel(command: CustomerReservationMutationCommand): string {
  if (command.kind === "CREATE") return `新建 ${command.request.customerName} 的预约`;
  if (command.kind === "SEAT") return `安排 ${command.baseline.customerName} 入座`;
  return `${actionLabels[command.request.action]}：${command.baseline.customerName}`;
}

function availableActions(
  state: CustomerReservationReadModel["state"],
): CustomerReservationTransitionAction[] {
  switch (state) {
    case "PENDING_CONFIRMATION":
      return ["CONFIRM", "CANCEL", "EXPIRE"];
    case "CONFIRMED":
      return ["ARRIVE", "CANCEL", "NO_SHOW", "EXPIRE"];
    case "WAITING":
      return ["CALL", "CANCEL"];
    case "CALLED":
      return ["RETRACT_CALL", "SKIP", "CANCEL"];
    default:
      return [];
  }
}

export function CustomerReservationDesk({
  operator,
  effectiveRoles,
  tables,
  reservationEnabled,
  customerSelfServiceEnabled,
  onSeated,
  onRecoveryViewChange,
}: {
  operator: { tenantId: string; storeId: string; id: string };
  effectiveRoles: readonly OperatorRole[];
  tables: readonly TableSnapshot[];
  reservationEnabled: boolean;
  customerSelfServiceEnabled: boolean;
  onSeated?: (result: CustomerReservationMutationResult) => void;
  onRecoveryViewChange?: (view: CustomerReservationRecoveryView) => void;
}): React.JSX.Element {
  const canWrite = effectiveRoles.includes("MANAGER") || effectiveRoles.includes("CASHIER");
  const effectiveCustomerSelfServiceEnabled = reservationEnabled && customerSelfServiceEnabled;
  const operatorIdentityKey = `${operator.tenantId}\0${operator.storeId}\0${operator.id}`;
  const currentOperatorIdentityKey = useRef(operatorIdentityKey);
  currentOperatorIdentityKey.current = operatorIdentityKey;
  const context = useMemo(() => customerReservationRecoveryContext(operator), [operator]);
  const [workspaceState, setWorkspaceState] = useState<{
    identityKey: string;
    value: CustomerReservationWorkspace;
  } | null>(null);
  const workspace =
    workspaceState?.identityKey === operatorIdentityKey ? workspaceState.value : null;
  const [historyState, setHistoryState] = useState<{
    identityKey: string;
    items: CustomerReservationWorkspaceItem[];
    totalItems: number;
    nextCursor: string | null;
    boundary: CustomerReservationHistoryPageBoundary;
  } | null>(null);
  const history = historyState?.identityKey === operatorIdentityKey ? historyState : null;
  const [historyLoadingIdentityKey, setHistoryLoadingIdentityKey] = useState<string | null>(null);
  const [historyLoadingMoreIdentityKey, setHistoryLoadingMoreIdentityKey] = useState<string | null>(
    null,
  );
  const [historyError, setHistoryError] = useState<{
    identityKey: string;
    text: string;
  } | null>(null);
  const [noShowClock, setNoShowClock] = useState<{
    identityKey: string;
    generatedAtMs: number;
    observedMonotonicMs: number;
    tick: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{
    kind: "SUCCESS" | "ERROR" | "INFO";
    text: string;
  } | null>(null);
  const [customerEntryCopyFeedback, setCustomerEntryCopyFeedback] = useState<{
    kind: "SUCCESS" | "ERROR";
    text: string;
  } | null>(null);
  const [customerEntryState, setCustomerEntryState] = useState<
    | { identityKey: string; status: "DISABLED" }
    | { identityKey: string; status: "LOADING" }
    | { identityKey: string; status: "READY"; storeName: string; url: string }
    | { identityKey: string; status: "ERROR"; text: string }
  >({
    identityKey: operatorIdentityKey,
    status: effectiveCustomerSelfServiceEnabled ? "LOADING" : "DISABLED",
  });
  const customerEntryView =
    customerEntryState.identityKey === operatorIdentityKey
      ? customerEntryState
      : effectiveCustomerSelfServiceEnabled
        ? ({ identityKey: operatorIdentityKey, status: "LOADING" } as const)
        : ({ identityKey: operatorIdentityKey, status: "DISABLED" } as const);
  const [recoveryView, setRecoveryViewState] = useState<CustomerReservationRecoveryView>(() =>
    readCustomerReservationRecovery(context),
  );
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [partySize, setPartySize] = useState("2");
  const [requestedStart, setRequestedStart] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("60");
  const [note, setNote] = useState("");
  const [workspaceView, setWorkspaceView] = useState<"LIST" | "CALENDAR">("LIST");
  const [calendarDate, setCalendarDate] = useState("");
  const [calendarRefreshRevision, setCalendarRefreshRevision] = useState(0);
  const [calendarState, setCalendarState] = useState<CustomerReservationCalendarState | null>(null);
  const [listFocusReservationId, setListFocusReservationId] = useState<string | null>(null);
  const [reasonByReservation, setReasonByReservation] = useState<Record<string, string>>({});
  const [tableByReservation, setTableByReservation] = useState<Record<string, string>>({});
  const initializedWorkspaceIdentity = useRef<string | null>(null);
  const reloadGeneration = useRef(0);
  const historyGeneration = useRef(0);
  const calendarGeneration = useRef(0);
  const reservationRowRefs = useRef(new Map<string, HTMLElement>());
  const readingRecoveryRef = useRef<string | null>(null);
  const completedRef = useRef(onSeated);
  completedRef.current = onSeated;

  useEffect(() => {
    if (!workspace || noShowClock?.identityKey !== operatorIdentityKey) return;
    const authoritativeNowMs =
      noShowClock.generatedAtMs + Math.max(0, performance.now() - noShowClock.observedMonotonicMs);
    const nextEligibleAt = workspace.items
      .filter((item) => item.reservation.state === "CONFIRMED")
      .map((item) => Date.parse(item.noShowEligibleAt))
      .filter((eligibleAt) => Number.isFinite(eligibleAt) && eligibleAt > authoritativeNowMs)
      .sort((left, right) => left - right)[0];
    if (nextEligibleAt === undefined) return;
    const timer = window.setTimeout(
      () =>
        setNoShowClock((current) =>
          current?.identityKey === operatorIdentityKey
            ? { ...current, tick: current.tick + 1 }
            : current,
        ),
      Math.max(10, Math.min(2_147_483_647, nextEligibleAt - authoritativeNowMs + 10)),
    );
    return () => window.clearTimeout(timer);
  }, [noShowClock, operatorIdentityKey, workspace]);

  const setRecoveryView = useCallback(
    (view: CustomerReservationRecoveryView): void => {
      setRecoveryViewState(view);
      onRecoveryViewChange?.(view);
    },
    [onRecoveryViewChange],
  );

  const reloadActive = useCallback(async (): Promise<void> => {
    const requestedIdentityKey = `${operator.tenantId}\0${operator.storeId}\0${operator.id}`;
    const requestGeneration = reloadGeneration.current + 1;
    reloadGeneration.current = requestGeneration;
    const stillCurrent = (): boolean =>
      currentOperatorIdentityKey.current === requestedIdentityKey &&
      reloadGeneration.current === requestGeneration;
    setLoading(true);
    try {
      const result = await listCustomerReservations({
        tenantId: operator.tenantId,
        storeId: operator.storeId,
      });
      if (!stillCurrent()) return;
      setNoShowClock({
        identityKey: requestedIdentityKey,
        generatedAtMs: Date.parse(result.generatedAt),
        observedMonotonicMs: performance.now(),
        tick: 0,
      });
      setWorkspaceState({ identityKey: requestedIdentityKey, value: result });
    } catch (error) {
      if (!stillCurrent()) return;
      setWorkspaceState((current) =>
        current?.identityKey === requestedIdentityKey ? current : null,
      );
      setFeedback({
        kind: "ERROR",
        text: `预约列表读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。若页面已有完整列表，系统会继续保留；请检查门店连接后点“重新读取”。`,
      });
    } finally {
      if (stillCurrent()) setLoading(false);
    }
  }, [operator.id, operator.storeId, operator.tenantId]);

  const reloadHistory = useCallback(async (): Promise<void> => {
    const requestedIdentityKey = `${operator.tenantId}\0${operator.storeId}\0${operator.id}`;
    const requestGeneration = historyGeneration.current + 1;
    historyGeneration.current = requestGeneration;
    setHistoryLoadingIdentityKey(requestedIdentityKey);
    setHistoryLoadingMoreIdentityKey(null);
    setHistoryError(null);
    try {
      const page = await listCustomerReservationHistory(
        { tenantId: operator.tenantId, storeId: operator.storeId },
        undefined,
        null,
      );
      if (
        currentOperatorIdentityKey.current !== requestedIdentityKey ||
        historyGeneration.current !== requestGeneration
      ) {
        return;
      }
      setHistoryState({
        identityKey: requestedIdentityKey,
        items: page.items,
        totalItems: page.totalItems,
        nextCursor: page.nextCursor,
        boundary: page.boundary,
      });
    } catch (error) {
      if (
        currentOperatorIdentityKey.current !== requestedIdentityKey ||
        historyGeneration.current !== requestGeneration
      ) {
        return;
      }
      setHistoryError({
        identityKey: requestedIdentityKey,
        text: `已结束预约读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。已加载的预约不会被清空，请恢复门店连接后重试。`,
      });
    } finally {
      if (
        currentOperatorIdentityKey.current === requestedIdentityKey &&
        historyGeneration.current === requestGeneration
      ) {
        setHistoryLoadingIdentityKey(null);
      }
    }
  }, [operator.id, operator.storeId, operator.tenantId]);

  const reload = useCallback(async (): Promise<void> => {
    await Promise.all([reloadActive(), reloadHistory()]);
  }, [reloadActive, reloadHistory]);

  const loadMoreHistory = useCallback(async (): Promise<void> => {
    if (
      !history ||
      history.nextCursor === null ||
      historyLoadingIdentityKey === operatorIdentityKey ||
      historyLoadingMoreIdentityKey === operatorIdentityKey
    ) {
      return;
    }
    const requestedIdentityKey = operatorIdentityKey;
    const requestedHistory = history;
    const requestGeneration = historyGeneration.current + 1;
    historyGeneration.current = requestGeneration;
    setHistoryLoadingMoreIdentityKey(requestedIdentityKey);
    setHistoryError(null);
    try {
      const page = await listCustomerReservationHistory(
        { tenantId: operator.tenantId, storeId: operator.storeId },
        requestedHistory.nextCursor ?? undefined,
        requestedHistory.boundary,
      );
      if (
        currentOperatorIdentityKey.current !== requestedIdentityKey ||
        historyGeneration.current !== requestGeneration
      ) {
        return;
      }
      setHistoryState((current) => {
        if (current?.identityKey !== requestedIdentityKey || current !== requestedHistory) {
          return current;
        }
        const reservationIds = new Set(current.items.map((item) => item.reservation.reservationId));
        return {
          identityKey: requestedIdentityKey,
          items: [
            ...current.items,
            ...page.items.filter((item) => !reservationIds.has(item.reservation.reservationId)),
          ],
          totalItems: page.totalItems,
          nextCursor: page.nextCursor,
          boundary: page.boundary,
        };
      });
    } catch (error) {
      if (
        currentOperatorIdentityKey.current !== requestedIdentityKey ||
        historyGeneration.current !== requestGeneration
      ) {
        return;
      }
      setHistoryError({
        identityKey: requestedIdentityKey,
        text: `更多已结束预约读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。此前已确认的历史仍保留，可以稍后重试。`,
      });
    } finally {
      if (
        currentOperatorIdentityKey.current === requestedIdentityKey &&
        historyGeneration.current === requestGeneration
      ) {
        setHistoryLoadingMoreIdentityKey(null);
      }
    }
  }, [
    history,
    historyLoadingIdentityKey,
    historyLoadingMoreIdentityKey,
    operator.storeId,
    operator.tenantId,
    operatorIdentityKey,
  ]);

  const recoverAuthority = useCallback(
    async (recovery: CustomerReservationRecovery): Promise<void> => {
      if (recovery.state !== "OUTCOME_UNKNOWN") return;
      const requestKey = recovery.command.request.idempotencyKey;
      if (readingRecoveryRef.current === requestKey) return;
      readingRecoveryRef.current = requestKey;
      setFeedback({
        kind: "INFO",
        text: `正在用原请求号核对“${commandLabel(recovery.command)}”。结果明确前，相关预约和球台不能再次操作。`,
      });
      try {
        const result = await getCustomerReservationByRequest(requestKey, recovery.command);
        const resolution = reconcileCustomerReservationAuthority(recovery, context, result);
        const next = readCustomerReservationRecovery(context);
        setRecoveryView(next);
        if (resolution.status === "EXECUTED") {
          setFeedback({ kind: "SUCCESS", text: "门店已确认原预约操作完成，不会重复执行。" });
          if (resolution.result.operation === "SEAT") completedRef.current?.(resolution.result);
          await reload();
        } else if (resolution.status === "RETRY_ALLOWED") {
          setFeedback({
            kind: "INFO",
            text: "门店已明确原操作没有执行。只能使用保存的原请求号重试，不能修改内容或生成新请求号。",
          });
        } else {
          setFeedback({
            kind: "ERROR",
            text: "权威结果与本机保存的原预约无法对应。相关预约和球台继续锁定，请由店长核对。",
          });
        }
      } catch (error) {
        setFeedback({
          kind: "ERROR",
          text: `原预约结果读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。不要重复点击；请恢复门店连接后重新进入预约工作台。`,
        });
      } finally {
        readingRecoveryRef.current = null;
      }
    },
    [context, reload, setRecoveryView],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    currentOperatorIdentityKey.current = operatorIdentityKey;
    setWorkspaceState(null);
    setHistoryState(null);
    setHistoryLoadingIdentityKey((current) => (current === operatorIdentityKey ? current : null));
    setHistoryLoadingMoreIdentityKey((current) =>
      current === operatorIdentityKey ? current : null,
    );
    setHistoryError((current) => (current?.identityKey === operatorIdentityKey ? current : null));
    setLoading(true);
    setFeedback(null);
    setCustomerName("");
    setCustomerMobile("");
    setPartySize("2");
    setCalendarDate("");
    setCalendarState(null);
    setListFocusReservationId(null);
    setRequestedStart("");
    setDurationMinutes("60");
    setNote("");
    setReasonByReservation({});
    setTableByReservation({});
    initializedWorkspaceIdentity.current = null;
  }, [operatorIdentityKey]);

  useEffect(() => {
    if (
      !workspace ||
      workspace.tenantId !== operator.tenantId ||
      workspace.storeId !== operator.storeId
    ) {
      return;
    }
    if (initializedWorkspaceIdentity.current !== operatorIdentityKey) {
      initializedWorkspaceIdentity.current = operatorIdentityKey;
      setCalendarDate(workspace.currentBusinessDate);
      setRequestedStart(defaultStartValue(workspace.generatedAt, workspace.timeZone));
    } else if (!calendarDate) {
      setCalendarDate(workspace.currentBusinessDate);
    }
  }, [calendarDate, operator.storeId, operator.tenantId, operatorIdentityKey, workspace]);

  const calendarWorkspaceIdentity =
    workspace?.tenantId === operator.tenantId && workspace.storeId === operator.storeId
      ? operatorIdentityKey
      : null;
  const calendarRequestKey = `${operatorIdentityKey}\0${calendarDate}`;
  const selectedCalendarState =
    calendarState?.requestKey === calendarRequestKey ? calendarState : null;
  const selectedCalendar = selectedCalendarState?.value ?? null;

  useEffect(() => {
    const requestGeneration = calendarGeneration.current + 1;
    calendarGeneration.current = requestGeneration;
    if (workspaceView !== "CALENDAR" || !calendarDate || !calendarWorkspaceIdentity) return;
    const requestedIdentityKey = operatorIdentityKey;
    const requestedDate = calendarDate;
    const requestedKey = `${requestedIdentityKey}\0${requestedDate}`;
    const requestedRefreshRevision = calendarRefreshRevision;
    const abortController = new AbortController();
    setCalendarState((current) => {
      const previous = current?.requestKey === requestedKey ? current.value : null;
      return {
        requestKey: requestedKey,
        refreshRevision: requestedRefreshRevision,
        status: previous ? "REFRESHING" : "LOADING",
        value: previous,
        error: null,
      };
    });
    void getCustomerReservationCalendarDay(
      { tenantId: operator.tenantId, storeId: operator.storeId },
      requestedDate,
      abortController.signal,
    )
      .then((value) => {
        if (
          abortController.signal.aborted ||
          currentOperatorIdentityKey.current !== requestedIdentityKey ||
          calendarGeneration.current !== requestGeneration
        ) {
          return;
        }
        setCalendarState({
          requestKey: requestedKey,
          refreshRevision: requestedRefreshRevision,
          status: "READY",
          value,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (
          abortController.signal.aborted ||
          currentOperatorIdentityKey.current !== requestedIdentityKey ||
          calendarGeneration.current !== requestGeneration
        ) {
          return;
        }
        const message = businessErrorMessage(error, "系统没有返回明确原因");
        setCalendarState((current) => {
          const previous = current?.requestKey === requestedKey ? current.value : null;
          return {
            requestKey: requestedKey,
            refreshRevision: requestedRefreshRevision,
            status: previous ? "STALE" : "ERROR",
            value: previous,
            error: message,
          };
        });
      });
    return () => abortController.abort();
  }, [
    calendarDate,
    calendarRefreshRevision,
    calendarWorkspaceIdentity,
    operator.storeId,
    operator.tenantId,
    operatorIdentityKey,
    workspaceView,
  ]);

  useEffect(() => {
    if (workspaceView !== "LIST" || !listFocusReservationId) return;
    const row = reservationRowRefs.current.get(listFocusReservationId);
    if (!row) return;
    row.scrollIntoView?.({ behavior: "smooth", block: "center" });
    row.focus({ preventScroll: true });
    setListFocusReservationId(null);
  }, [listFocusReservationId, workspaceView]);

  useEffect(() => {
    const current = readCustomerReservationRecovery(context);
    setRecoveryView(current);
    if (current.status === "OWNED" && current.recovery.state === "OUTCOME_UNKNOWN") {
      void recoverAuthority(current.recovery);
    } else if (current.status === "CONTEXT_MISMATCH") {
      setFeedback({
        kind: "ERROR",
        text: "本机保存着其他员工的预约原请求。当前账号只能查看，不能替换或重试；请切回原账号核对。",
      });
    } else if (current.status === "RECOVERY_STORAGE_UNAVAILABLE") {
      setFeedback({
        kind: "ERROR",
        text: "浏览器无法安全读取预约保护记录。为防止重复预约或重复开台，所有预约写操作已暂停。",
      });
    }
  }, [context, recoverAuthority, setRecoveryView]);

  const dispatch = async (recovery: CustomerReservationRecovery): Promise<void> => {
    if (!beginCustomerReservationDispatch(recovery, context)) {
      setFeedback({ kind: "ERROR", text: "原预约请求当前不能安全提交，页面没有发出新请求。" });
      return;
    }
    setLoading(true);
    try {
      const result =
        recovery.command.kind === "CREATE"
          ? await createCustomerReservation(recovery.command)
          : recovery.command.kind === "TRANSITION"
            ? await transitionCustomerReservation(recovery.command)
            : await seatCustomerReservation(recovery.command);
      if (!clearCustomerReservationRecovery(recovery, context)) {
        setFeedback({
          kind: "ERROR",
          text: "门店已返回成功，但浏览器无法清除保护记录。相关预约继续锁定，请勿再次操作。",
        });
        return;
      }
      setRecoveryView(readCustomerReservationRecovery(context));
      setFeedback({
        kind: "SUCCESS",
        text:
          result.operation === "CREATE"
            ? `预约已创建，编号 ${result.reservation.reservationNumber}。本轮没有收订金，也没有附带优惠。`
            : result.operation === "SEAT"
              ? `${result.reservation.customerName} 已安排到 ${result.seating?.tableNumber} 号台并完成开台。`
              : `${actionLabels[result.operation]}已完成。`,
      });
      if (result.operation === "SEAT") completedRef.current?.(result);
      if (result.operation === "CREATE") {
        setCustomerName("");
        setCustomerMobile("");
        setNote("");
      }
      await reload();
    } catch (error) {
      if (customerReservationOutcomeIsUnknown(error)) {
        markCustomerReservationOutcomeUnknown(recovery, context);
        const next = readCustomerReservationRecovery(context);
        setRecoveryView(next);
        setFeedback({
          kind: "INFO",
          text: "预约操作结果未知。系统正在先查询原请求结果，确认前不会允许相关预约或球台再次操作。",
        });
        if (next.status === "OWNED") await recoverAuthority(next.recovery);
      } else {
        clearCustomerReservationRecovery(recovery, context);
        setRecoveryView(readCustomerReservationRecovery(context));
        setFeedback({
          kind: "ERROR",
          text: `预约操作没有执行：${businessErrorMessage(error, "系统没有返回明确原因")}。请按提示刷新预约或重新选择空闲球台。`,
        });
      }
    } finally {
      finishCustomerReservationDispatch(recovery, context);
      setLoading(false);
    }
  };

  const claimAndDispatch = (command: CustomerReservationMutationCommand): void => {
    const claimed = claimCustomerReservationRecovery(context, command);
    setRecoveryView(claimed);
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        text: "无法安全保存这次预约的原请求，系统没有发出操作。请保留页面并先处理已有待确认请求。",
      });
      return;
    }
    void dispatch(claimed.recovery);
  };

  const create = (): void => {
    const normalizedName = customerName.trim();
    const normalizedMobile = customerMobile.trim();
    const size = Number(partySize);
    const duration = Number(durationMinutes);
    const normalizedNote = note.trim();
    if (!normalizedName || [...normalizedName].length > 60) {
      setFeedback({ kind: "ERROR", text: "顾客姓名不能为空，最多 60 个字；请修改姓名后再创建。" });
      return;
    }
    if (!/^1[3-9][0-9]{9}$/u.test(normalizedMobile)) {
      setFeedback({ kind: "ERROR", text: "手机号必须是 11 位中国大陆手机号，例如 13800138000。" });
      return;
    }
    if (!Number.isInteger(size) || size < 1 || size > 20) {
      setFeedback({ kind: "ERROR", text: "预约人数必须是 1 至 20 人的整数。" });
      return;
    }
    if (!workspace) {
      setFeedback({
        kind: "ERROR",
        text: "门店时间尚未读取成功，系统没有创建预约；请先刷新预约工作台。",
      });
      return;
    }
    const start = storeLocalDateTimeToIso(requestedStart, workspace.timeZone);
    if (start.status !== "VALID") {
      setFeedback(
        start.status === "AMBIGUOUS"
          ? {
              kind: "ERROR",
              text: "所选时间在门店当地会出现两次，请改选前后相邻的时间后再创建预约。",
            }
          : { kind: "ERROR", text: "请选择门店当地真实存在的预约开始时间。" },
      );
      return;
    }
    if (!Number.isInteger(duration) || duration < 30 || duration > 720 || duration % 30 !== 0) {
      setFeedback({
        kind: "ERROR",
        text: "预计时长必须按 30 分钟递增，范围是 30 分钟至 12 小时。",
      });
      return;
    }
    if ([...normalizedNote].length > 300) {
      setFeedback({ kind: "ERROR", text: "备注最多 300 个字，请删减后再创建。" });
      return;
    }
    claimAndDispatch({
      kind: "CREATE",
      context,
      request: {
        contractVersion: "1.0.0",
        customerName: normalizedName,
        customerMobile: normalizedMobile,
        partySize: size,
        requestedStartAt: start.value,
        durationMinutes: duration,
        note: normalizedNote || null,
        depositFen: 0,
        discountFen: 0,
        idempotencyKey: nextIdempotencyKey(),
      },
    });
  };

  const transition = (
    reservation: CustomerReservationReadModel,
    action: CustomerReservationTransitionAction,
  ): void => {
    const reason = reasonByReservation[reservation.reservationId]?.trim() ?? "";
    if (reasonActions.has(action) && (reason.length < 2 || [...reason].length > 200)) {
      setFeedback({
        kind: "ERROR",
        text: `${actionLabels[action]}需要填写 2 至 200 个字的原因，说明顾客或门店发生了什么。`,
      });
      return;
    }
    claimAndDispatch({
      kind: "TRANSITION",
      reservationId: reservation.reservationId,
      baseline: reservation,
      context,
      request: reasonActions.has(action)
        ? {
            action: action as "SKIP" | "RETRACT_CALL" | "CANCEL" | "NO_SHOW" | "EXPIRE",
            expectedReservationVersion: reservation.reservationVersion,
            reason,
            idempotencyKey: nextIdempotencyKey(),
          }
        : {
            action: action as "CONFIRM" | "ARRIVE" | "CALL",
            expectedReservationVersion: reservation.reservationVersion,
            idempotencyKey: nextIdempotencyKey(),
          },
    });
  };

  const seat = (reservation: CustomerReservationReadModel): void => {
    const tableId = tableByReservation[reservation.reservationId] ?? "";
    const table = tables.find((candidate) => candidate.tableId === tableId);
    if (
      !table ||
      table.tenantId !== operator.tenantId ||
      table.storeId !== operator.storeId ||
      table.state !== "IDLE" ||
      table.sessionId !== null
    ) {
      setFeedback({
        kind: "ERROR",
        text: "请选择当前仍为空闲的球台；若球台已开台，请重新读取后换一张。",
      });
      return;
    }
    claimAndDispatch({
      kind: "SEAT",
      reservationId: reservation.reservationId,
      baseline: reservation,
      table,
      context,
      request: {
        expectedReservationVersion: reservation.reservationVersion,
        tableId: table.tableId,
        expectedTableRevision: table.revision,
        idempotencyKey: nextIdempotencyKey(),
      },
    });
  };

  const idleTables = tables.filter(
    (table) =>
      table.tenantId === operator.tenantId &&
      table.storeId === operator.storeId &&
      table.state === "IDLE" &&
      table.sessionId === null,
  );
  const recoveryLocked = recoveryView.status !== "NONE";
  const workspaceTimeZone = workspace?.timeZone ?? "UTC";
  const calendarTimeZone = selectedCalendar?.timeZone ?? workspaceTimeZone;
  const calendarSlots = useMemo(() => {
    const slots = new Map<string, CustomerReservationWorkspaceItem[]>();
    for (const item of selectedCalendar?.items ?? []) {
      const time = displayClock(item.reservation.requestedStartAt, calendarTimeZone);
      const entries = slots.get(time) ?? [];
      entries.push(item);
      slots.set(time, entries);
    }
    return [...slots.entries()];
  }, [calendarTimeZone, selectedCalendar]);

  const loadCustomerEntry = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const requestedIdentityKey = `${operator.tenantId}\0${operator.storeId}\0${operator.id}`;
      if (!effectiveCustomerSelfServiceEnabled) {
        setCustomerEntryState({ identityKey: requestedIdentityKey, status: "DISABLED" });
        setCustomerEntryCopyFeedback(null);
        return;
      }
      setCustomerEntryState({ identityKey: requestedIdentityKey, status: "LOADING" });
      setCustomerEntryCopyFeedback(null);
      try {
        const bootstrap = await getCustomerSelfServiceBootstrap(signal);
        if (signal?.aborted || currentOperatorIdentityKey.current !== requestedIdentityKey) return;
        if (bootstrap.tenantId !== operator.tenantId || bootstrap.storeId !== operator.storeId) {
          setCustomerEntryState({
            identityKey: requestedIdentityKey,
            status: "ERROR",
            text: "门店返回的顾客入口不属于当前门店。系统没有显示或编码该链接，请联系店长核对门店配置。",
          });
          return;
        }
        setCustomerEntryState({
          identityKey: requestedIdentityKey,
          status: "READY",
          storeName: bootstrap.storeName,
          url: bootstrap.customerEntryUrl,
        });
      } catch (error) {
        if (signal?.aborted || currentOperatorIdentityKey.current !== requestedIdentityKey) return;
        setCustomerEntryState({
          identityKey: requestedIdentityKey,
          status: "ERROR",
          text: `顾客入口读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。页面没有生成猜测链接，请恢复门店连接后重新读取。`,
        });
      }
    },
    [effectiveCustomerSelfServiceEnabled, operator.id, operator.storeId, operator.tenantId],
  );

  const copyCustomerEntryUrl = useCallback(async (customerEntryUrl: string): Promise<void> => {
    setCustomerEntryCopyFeedback(null);
    try {
      if (typeof navigator.clipboard?.writeText !== "function") {
        throw new Error("clipboard unavailable");
      }
      await navigator.clipboard.writeText(customerEntryUrl);
      setCustomerEntryCopyFeedback({ kind: "SUCCESS", text: "顾客预约链接已复制。" });
    } catch {
      setCustomerEntryCopyFeedback({
        kind: "ERROR",
        text: "无法自动复制。请长按或点击上方完整链接，全选后手动复制。",
      });
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    void loadCustomerEntry(abortController.signal);
    return () => abortController.abort();
  }, [loadCustomerEntry]);

  return (
    <section className="customer-reservation-desk" aria-label="预约工作台">
      <header className="customer-reservation-heading">
        <div>
          <span className="eyebrow">预约工作台</span>
          <h2>预约、到店排队与入座</h2>
          <p>统一处理电话或现场预约。预约本身不计算台费，入座时由门店开台流程创建正式台单。</p>
        </div>
        <button
          type="button"
          className="icon-button"
          title="重新读取预约"
          aria-label="重新读取预约"
          disabled={loading}
          onClick={() => void reload()}
        >
          <RefreshCw size={18} />
        </button>
      </header>

      {!reservationEnabled ? (
        <output className="disabled-reason">
          <strong>门店预约当前已关闭</strong>
          <span>
            现在不能创建新预约；下方已有预约仍可继续确认、登记到店、叫号、取消或安排入座，其他开台和经营功能不受影响。
          </span>
        </output>
      ) : null}

      <section className="customer-reservation-entry" aria-label="顾客预约入口">
        <div className="customer-reservation-entry-heading">
          <h3>顾客预约入口</h3>
          <p>让顾客用自己的手机扫码，或把下方完整链接发给顾客。</p>
        </div>
        {customerEntryView.status === "DISABLED" ? (
          <output className="customer-reservation-entry-status">
            顾客扫码自助预约当前已关闭。页面不会生成二维码或公开链接；老板或店长可在“门店设置”中先开启“门店预约”，再开启“顾客扫码自助预约”。
          </output>
        ) : customerEntryView.status === "LOADING" ? (
          <output className="customer-reservation-entry-status">
            正在读取本店公开入口，请稍候……
          </output>
        ) : customerEntryView.status === "ERROR" ? (
          <div className="customer-reservation-entry-status is-error" role="alert">
            <span>{customerEntryView.text}</span>
            <button
              type="button"
              className="button secondary"
              onClick={() => void loadCustomerEntry()}
            >
              重新读取顾客入口
            </button>
          </div>
        ) : (
          <div className="customer-reservation-entry-layout">
            <div className="customer-reservation-entry-qr">
              <QRCodeSVG
                value={customerEntryView.url}
                size={144}
                title={`${customerEntryView.storeName}顾客预约入口二维码`}
                role="img"
                aria-label={`${customerEntryView.storeName}顾客预约入口二维码`}
                data-qr-value={customerEntryView.url}
              />
            </div>
            <div className="customer-reservation-entry-copy">
              <label>
                完整链接
                <input
                  className="customer-reservation-entry-link"
                  aria-label="顾客预约完整链接"
                  readOnly
                  value={customerEntryView.url}
                  onFocus={(event) => event.currentTarget.select()}
                  onClick={(event) => event.currentTarget.select()}
                />
                <small>可点击或长按选择完整链接；无法自动复制时可在这里全选并手动复制。</small>
              </label>
              <button
                type="button"
                className="button secondary"
                onClick={() => void copyCustomerEntryUrl(customerEntryView.url)}
              >
                复制顾客预约链接
              </button>
              {customerEntryCopyFeedback ? (
                <output
                  className={`customer-reservation-entry-copy-feedback ${
                    customerEntryCopyFeedback.kind === "SUCCESS" ? "is-success" : "is-error"
                  }`}
                >
                  {customerEntryCopyFeedback.text}
                </output>
              ) : null}
            </div>
          </div>
        )}
        <ul className="customer-reservation-entry-notes">
          <li>顾客打开后会先看到本店名称，再登记本次访客。</li>
          <li>手机号只用于本次访客服务，不会自动成为会员。</li>
          <li>预约不收订金、不占用球台；提交后仍需员工核对确认。</li>
        </ul>
      </section>

      <div className="reservation-money-boundary">
        <strong>本轮不收订金，也不附带优惠</strong>
        <span>
          创建预约只记录到店时间和人数，不会收款、抵扣台费或承诺折扣；正式费用从入座开台后按门店计费规则产生。
        </span>
      </div>

      {feedback ? (
        <output className={feedback.kind === "SUCCESS" ? "success-band" : "error-band"}>
          {feedback.kind === "SUCCESS" ? <Check size={18} /> : <Clock3 size={18} />}
          <span>{feedback.text}</span>
        </output>
      ) : null}

      {recoveryView.status === "OWNED" ? (
        <div className="reservation-recovery" role="alert">
          <strong>原操作待确认：{commandLabel(recoveryView.recovery.command)}</strong>
          <span>页面保留了原内容和原请求号。结果明确前不能修改相关预约或球台。</span>
          {canWrite && recoveryView.canRetry ? (
            <button
              type="button"
              className="button secondary"
              onClick={() => void dispatch(recoveryView.recovery)}
            >
              使用原请求安全重试
            </button>
          ) : null}
        </div>
      ) : recoveryView.status !== "NONE" ? (
        <div className="reservation-recovery" role="alert">
          当前账号不能处理本机保存的预约原请求，预约写操作已暂停；请切回原账号核对。
        </div>
      ) : null}

      {canWrite && reservationEnabled ? (
        <section className="reservation-create" aria-label="新建预约">
          <div className="reservation-section-title">
            <h3>新建预约</h3>
            <p>填写顾客到店计划；创建后先处于“待确认”，需要工作人员再次确认门店接受预约。</p>
          </div>
          <div className="reservation-form-grid">
            <label>
              顾客姓名
              <input
                value={customerName}
                maxLength={60}
                onChange={(event) => setCustomerName(event.target.value)}
              />
              <small>用于现场叫号和核对，最多 60 个字。</small>
            </label>
            <label>
              手机号
              <input
                inputMode="tel"
                value={customerMobile}
                maxLength={11}
                onChange={(event) => setCustomerMobile(event.target.value)}
              />
              <small>填写 11 位大陆手机号，方便确认到店情况。</small>
            </label>
            <label>
              到店人数
              <input
                type="number"
                min="1"
                max="20"
                value={partySize}
                onChange={(event) => setPartySize(event.target.value)}
              />
              <small>填写实际同行人数，范围 1 至 20 人。</small>
            </label>
            <label>
              预计开始时间
              <input
                type="datetime-local"
                value={requestedStart}
                onChange={(event) => setRequestedStart(event.target.value)}
              />
              <small>这是顾客计划到店时间，不代表球台已经保留或开始计费。</small>
            </label>
            <label>
              预计使用时长
              <select
                value={durationMinutes}
                onChange={(event) => setDurationMinutes(event.target.value)}
              >
                {Array.from({ length: 24 }, (_, index) => (index + 1) * 30).map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {minutes < 60 ? `${minutes} 分钟` : `${minutes / 60} 小时`}
                  </option>
                ))}
              </select>
              <small>按 30 分钟递增，最长 12 小时；只用于安排，不计算费用。</small>
            </label>
            <label className="reservation-note-field">
              备注（可不填）
              <textarea
                value={note}
                maxLength={300}
                onChange={(event) => setNote(event.target.value)}
              />
              <small>可写偏好或联系说明，不要在这里承诺折扣或记录收款。</small>
            </label>
          </div>
          <button
            type="button"
            className="button primary"
            disabled={loading || recoveryLocked || !workspace}
            onClick={create}
          >
            创建待确认预约
          </button>
        </section>
      ) : canWrite ? (
        <div className="disabled-reason">
          门店预约已关闭，不能创建新预约；已有预约的后续处理按钮仍可正常使用。
        </div>
      ) : (
        <div className="disabled-reason">
          老板账号可查看预约和排队状态；创建、状态处理和入座请由店长或收银员办理。
        </div>
      )}

      <div className="reservation-view-toolbar">
        <fieldset className="reservation-view-switch">
          <legend className="visually-hidden">预约查看方式</legend>
          <button
            type="button"
            aria-pressed={workspaceView === "LIST"}
            onClick={() => setWorkspaceView("LIST")}
          >
            <ListIcon size={16} /> 列表
          </button>
          <button
            type="button"
            aria-pressed={workspaceView === "CALENDAR"}
            onClick={() => setWorkspaceView("CALENDAR")}
          >
            <CalendarClock size={16} /> 日历
          </button>
        </fieldset>
        {workspace ? (
          <span>当前空闲球台 {workspace.idleTableCount} 张 · 候台顺序和等待说明由门店返回</span>
        ) : null}
      </div>

      {workspaceView === "LIST" ? (
        <section className="reservation-list" aria-label="预约列表">
          <div className="reservation-section-title">
            <div>
              <h3>当前预约</h3>
              <p>
                {workspace
                  ? `${workspace.items.length} 条预约；队列顺序以门店当前返回结果为准。`
                  : "正在等待门店返回预约列表。"}
              </p>
            </div>
          </div>
          {workspace?.waitEstimateBasis === "CURRENT_IDLE_TABLES_AND_PLANNED_DURATIONS" ? (
            <p className="reservation-wait-basis">
              等待时间只按当前空闲球台和预约计划时长估算，不保证实际释放时间；在用台结束时间未知时不会猜测。
            </p>
          ) : null}
          {loading && !workspace ? <div className="loading-state">正在读取预约...</div> : null}
          {workspace?.items.length === 0 ? <div className="empty-state">当前没有预约</div> : null}
          {workspace?.items.map((item) => {
            const { reservation, queuePosition } = item;
            const waitEstimate = reservationWaitEstimateText(item);
            const nextWaiting = workspace.nextWaitingReservationId === reservation.reservationId;
            const actions = availableActions(reservation.state);
            const needsReason = actions.some((action) => reasonActions.has(action));
            const noShowEligibleAt = Date.parse(item.noShowEligibleAt);
            const authoritativeNowMs =
              noShowClock?.identityKey === operatorIdentityKey
                ? noShowClock.generatedAtMs +
                  Math.max(0, performance.now() - noShowClock.observedMonotonicMs)
                : Number.NEGATIVE_INFINITY;
            const noShowBlocked =
              reservation.state === "CONFIRMED" &&
              Number.isFinite(noShowEligibleAt) &&
              authoritativeNowMs < noShowEligibleAt;
            const noShowEligibleTime = displayTime(item.noShowEligibleAt, workspaceTimeZone);
            return (
              <article
                className="reservation-row"
                data-reservation-id={reservation.reservationId}
                key={reservation.reservationId}
                ref={(node) => {
                  if (node) reservationRowRefs.current.set(reservation.reservationId, node);
                  else reservationRowRefs.current.delete(reservation.reservationId);
                }}
                tabIndex={-1}
              >
                <div className="reservation-row-main">
                  <div>
                    <strong>{reservation.customerName}</strong>
                    <span className="status-badge">{stateLabels[reservation.state]}</span>
                    {queuePosition ? (
                      <span className="queue-badge">排队第 {queuePosition} 位</span>
                    ) : null}
                    {nextWaiting ? <span className="next-waiting-badge">下一位</span> : null}
                  </div>
                  <dl>
                    <div>
                      <dt>
                        <Phone size={14} /> 手机
                      </dt>
                      <dd>{reservation.customerMobile}</dd>
                    </div>
                    <div>
                      <dt>
                        <UsersRound size={14} /> 人数
                      </dt>
                      <dd>{reservation.partySize} 人</dd>
                    </div>
                    <div>
                      <dt>
                        <CalendarClock size={14} /> 到店
                      </dt>
                      <dd>{displayTime(reservation.requestedStartAt, workspaceTimeZone)}</dd>
                    </div>
                    <div>
                      <dt>预约编号</dt>
                      <dd>{reservation.reservationNumber}</dd>
                    </div>
                  </dl>
                  <p>
                    预约来源：
                    {reservation.source === "SELF_SERVICE" ? "顾客自助提交" : "前台代客创建"}
                  </p>
                  {reservation.source === "SELF_SERVICE" &&
                  reservation.state === "PENDING_CONFIRMATION" ? (
                    <p className="reservation-money-boundary">
                      顾客刚提交，仍需员工核对到店时间和人数后再确认；当前没有确认预约，也没有占用球台。
                    </p>
                  ) : null}
                  {reservation.note ? <p>备注：{reservation.note}</p> : null}
                  {reservation.state === "CONFIRMED" ? (
                    <p className="reservation-no-show-eligibility">
                      <Clock3 size={14} aria-hidden="true" />
                      <span>
                        最早可登记爽约：{noShowEligibleTime}（预约时间后 {item.noShowGraceMinutes}{" "}
                        分钟）
                      </span>
                    </p>
                  ) : null}
                  {noShowBlocked ? (
                    <p className="reservation-money-boundary">
                      现在还不能登记爽约；请到 {noShowEligibleTime}{" "}
                      后再操作，宽限时间内继续等待顾客到店。
                    </p>
                  ) : null}
                  {waitEstimate ? (
                    <p
                      className="reservation-wait-estimate"
                      data-wait-estimate={item.waitEstimateKind}
                    >
                      <Clock3 size={14} aria-hidden="true" />
                      <span>{waitEstimate}</span>
                    </p>
                  ) : null}
                  {reservation.state === "SEATED" ? (
                    <p>
                      已安排 {reservation.assignedTableNumber} 号台，正式台单号{" "}
                      {reservation.orderNumber}。
                    </p>
                  ) : null}
                </div>

                {canWrite && actions.length > 0 ? (
                  <div className="reservation-actions">
                    {needsReason ? (
                      <label>
                        处理原因
                        <input
                          value={reasonByReservation[reservation.reservationId] ?? ""}
                          maxLength={200}
                          placeholder="例如：顾客来电取消，已复述确认"
                          onChange={(event) =>
                            setReasonByReservation((current) => ({
                              ...current,
                              [reservation.reservationId]: event.target.value,
                            }))
                          }
                        />
                        <small>撤回误叫号、跳过、取消、爽约或过期时必须填写 2 至 200 个字。</small>
                      </label>
                    ) : null}
                    {actions.map((action) => (
                      <div className="reservation-action" key={action}>
                        <button
                          type="button"
                          className="button secondary compact"
                          disabled={
                            loading || recoveryLocked || (action === "NO_SHOW" && noShowBlocked)
                          }
                          onClick={() => transition(reservation, action)}
                        >
                          {actionLabels[action]}
                        </button>
                        <small>
                          {action === "NO_SHOW" && noShowBlocked
                            ? `还不能登记爽约：最早要到 ${noShowEligibleTime}。`
                            : actionConsequences[action]}
                        </small>
                      </div>
                    ))}
                  </div>
                ) : null}

                {canWrite && reservation.state === "CALLED" ? (
                  <div className="reservation-seat">
                    <label>
                      选择空闲球台
                      <select
                        value={tableByReservation[reservation.reservationId] ?? ""}
                        onChange={(event) =>
                          setTableByReservation((current) => ({
                            ...current,
                            [reservation.reservationId]: event.target.value,
                          }))
                        }
                      >
                        <option value="">请选择</option>
                        {idleTables.map((table) => (
                          <option key={table.tableId} value={table.tableId}>
                            {table.tableNumber} 号台 · {table.area}
                          </option>
                        ))}
                      </select>
                      <small>
                        确认后会为这位顾客正式开台并创建台单；本预约没有订金或优惠可抵扣。
                      </small>
                    </label>
                    <button
                      type="button"
                      className="button primary"
                      disabled={loading || recoveryLocked || idleTables.length === 0}
                      onClick={() => seat(reservation)}
                    >
                      确认入座并开台
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>
      ) : (
        <section className="reservation-calendar" aria-label="预约日历">
          <div className="reservation-section-title">
            <div>
              <h3>按日查看预约</h3>
              <p>选择营业日期后，系统会向门店完整核对当天全部预约，再按计划到店时间排列。</p>
            </div>
            <div>
              <label className="reservation-calendar-date">
                查看日期
                <input
                  aria-label="日历日期"
                  type="date"
                  value={calendarDate}
                  onChange={(event) => setCalendarDate(event.target.value)}
                />
              </label>
              <button
                type="button"
                className="button secondary compact"
                disabled={
                  !calendarDate ||
                  !calendarWorkspaceIdentity ||
                  selectedCalendarState?.status === "LOADING" ||
                  selectedCalendarState?.status === "REFRESHING"
                }
                onClick={() => setCalendarRefreshRevision((current) => current + 1)}
              >
                重新完整核对日历
              </button>
            </div>
          </div>
          {!calendarDate ? (
            <div className="empty-state">请先选择一个营业日期。</div>
          ) : !workspace || !selectedCalendarState ? (
            <div className="loading-state">正在准备完整核对所选营业日的全部预约...</div>
          ) : null}
          {selectedCalendarState?.status === "LOADING" ? (
            <div className="loading-state">正在完整核对 {calendarDate} 的全部预约...</div>
          ) : null}
          {selectedCalendarState?.status === "REFRESHING" ? (
            <div className="loading-state">
              正在重新完整核对 {calendarDate} 的全部预约；当前暂时显示最近一次已确认结果。
            </div>
          ) : null}
          {selectedCalendarState?.status === "ERROR" ? (
            <div className="error-band" role="alert">
              <span>
                所选营业日预约完整核对失败：{selectedCalendarState.error}
                。当前无法确认有没有预约，页面没有显示旧日期的结果。
              </span>
              <button
                type="button"
                className="button secondary compact"
                onClick={() => setCalendarRefreshRevision((current) => current + 1)}
              >
                重新完整核对
              </button>
            </div>
          ) : null}
          {selectedCalendarState?.status === "STALE" ? (
            <div className="error-band" role="alert">
              <span>
                预约日历刷新失败：{selectedCalendarState.error}
                。当前显示最近一次已确认结果，最新结果未知。
              </span>
              <button
                type="button"
                className="button secondary compact"
                onClick={() => setCalendarRefreshRevision((current) => current + 1)}
              >
                再次完整核对
              </button>
            </div>
          ) : null}
          {selectedCalendar ? (
            <p className="form-hint">
              最近确认时间：{displayTime(selectedCalendar.generatedAt, selectedCalendar.timeZone)}
            </p>
          ) : null}
          {selectedCalendarState?.status === "READY" && calendarSlots.length === 0 ? (
            <div className="empty-state">
              已完整核对 {calendarDate}：当前没有预约，请以这次门店读取结果为准。
            </div>
          ) : null}
          <div className="reservation-calendar-slots">
            {calendarSlots.map(([time, items]) => (
              <section key={time} className="reservation-calendar-slot" aria-label={`${time} 预约`}>
                <time>{time}</time>
                <div>
                  {items.map(({ reservation }) => (
                    <article key={reservation.reservationId}>
                      <div>
                        <strong>{reservation.customerName}</strong>
                        <span className="status-badge">{stateLabels[reservation.state]}</span>
                      </div>
                      <span>
                        {reservation.partySize} 人 · 计划{" "}
                        {displayTime(reservation.requestedStartAt, calendarTimeZone)} 至{" "}
                        {displayTime(reservation.requestedEndAt, calendarTimeZone)}
                      </span>
                      {reservation.note ? <small>备注：{reservation.note}</small> : null}
                      {availableActions(reservation.state).length > 0 &&
                      canWrite &&
                      workspace?.items.some(
                        (item) =>
                          item.reservation.reservationId === reservation.reservationId &&
                          item.reservation.reservationVersion === reservation.reservationVersion,
                      ) ? (
                        <button
                          type="button"
                          className="button secondary compact"
                          onClick={() => {
                            setListFocusReservationId(reservation.reservationId);
                            setWorkspaceView("LIST");
                          }}
                        >
                          去列表处理
                        </button>
                      ) : availableActions(reservation.state).length === 0 ? (
                        <small>此预约已结束，仅供核对。</small>
                      ) : canWrite ? (
                        <small>预约状态比当前列表更新，请点页面右上角重新读取预约后再处理。</small>
                      ) : (
                        <small>当前账号可查看预约；状态处理请由店长或收银员办理。</small>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>
      )}

      <section className="reservation-list reservation-history" aria-label="已结束预约">
        <div className="reservation-section-title">
          <div>
            <h3>已结束预约</h3>
            <p>
              {history
                ? `已加载 ${history.items.length} / ${history.totalItems} 条；这里只显示已取消、已爽约、已过期或已入座结束的预约。`
                : "安全读取历史第一页后，会在这里显示已经结束的预约。"}
            </p>
          </div>
        </div>
        {historyLoadingIdentityKey === operatorIdentityKey && !history ? (
          <div className="loading-state">正在读取已结束预约...</div>
        ) : null}
        {historyError?.identityKey === operatorIdentityKey ? (
          <div className="error-band" role="alert">
            <span>{historyError.text}</span>
            {!history ? (
              <button
                type="button"
                className="button secondary compact"
                onClick={() => void reloadHistory()}
                disabled={historyLoadingIdentityKey === operatorIdentityKey}
              >
                重新读取已结束预约
              </button>
            ) : null}
          </div>
        ) : null}
        {history && history.items.length === 0 ? (
          <div className="empty-state">当前没有已结束预约</div>
        ) : null}
        {history?.items.map(({ reservation }) => (
          <article
            className="reservation-row reservation-history-row"
            key={reservation.reservationId}
          >
            <div className="reservation-row-main">
              <div>
                <strong>{reservation.customerName}</strong>
                <span className="status-badge">{stateLabels[reservation.state]}</span>
              </div>
              <dl>
                <div>
                  <dt>预约编号</dt>
                  <dd>{reservation.reservationNumber}</dd>
                </div>
                <div>
                  <dt>营业日期</dt>
                  <dd>{reservation.businessDate}</dd>
                </div>
                {workspace ? (
                  <div>
                    <dt>计划到店</dt>
                    <dd>{displayTime(reservation.requestedStartAt, workspaceTimeZone)}</dd>
                  </div>
                ) : null}
                {reservation.state === "SEATED" ? (
                  <>
                    <div>
                      <dt>已安排球台</dt>
                      <dd>{reservation.assignedTableNumber} 号台</dd>
                    </div>
                    <div>
                      <dt>正式台单号</dt>
                      <dd>{reservation.orderNumber}</dd>
                    </div>
                  </>
                ) : null}
              </dl>
              <p>
                预约来源：
                {reservation.source === "SELF_SERVICE" ? "顾客自助提交" : "前台代客创建"}
              </p>
            </div>
          </article>
        ))}
        {history?.nextCursor ? (
          <button
            type="button"
            className="button secondary"
            onClick={() => void loadMoreHistory()}
            disabled={
              historyLoadingIdentityKey === operatorIdentityKey ||
              historyLoadingMoreIdentityKey === operatorIdentityKey
            }
          >
            {historyLoadingMoreIdentityKey === operatorIdentityKey
              ? "正在加载更多已结束预约..."
              : "加载更多已结束预约"}
          </button>
        ) : history && history.items.length > 0 ? (
          <p className="form-hint">已加载全部已结束预约。</p>
        ) : null}
      </section>
    </section>
  );
}
